import type { PassportPageSummary } from "@peer-start/platform-contracts";
import { ProgressBar } from "@peer-start/ui";
import { ChevronDown } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { skillProgressForPage, type ProfileSkillRow } from "./profile-skills.js";

export function ProfileSkillsAside({
  pages,
  skills,
  expandedIds,
  onToggle,
}: {
  pages: PassportPageSummary[];
  skills: ProfileSkillRow[];
  expandedIds: Set<string>;
  onToggle: (pageId: string) => void;
}) {
  const { t } = useTranslation();
  const groups = useMemo(
    () =>
      pages
        .map((summary) => ({
          page: summary.page,
          skills: skills.filter((skill) => skill.pageIds.includes(summary.page.id)),
        }))
        .filter((group) => group.skills.length > 0),
    [pages, skills],
  );

  return (
    <section
      aria-label={t("profile.yourSkills")}
      className="h-fit self-start overflow-hidden rounded-2xl border border-border bg-background"
    >
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-base font-medium">{t("profile.yourSkills")}</h2>
      </div>
      <div className="space-y-3 px-5 py-5">
        {groups.length ? (
          groups.map((group) => {
            const open = expandedIds.has(group.page.id);
            const progress = skillProgressForPage(group.page.id, skills);
            return (
              <details
                className="group/item min-w-0 rounded-2xl border border-border/70 bg-background open:shadow-xs"
                key={group.page.id}
                open={open}
              >
                <summary
                  className="flex cursor-pointer list-none items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                  data-control-id={`profile.skills.aside.group.${group.page.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    onToggle(group.page.id);
                  }}
                >
                  <span className="min-w-0 flex-1">{group.page.title}</span>
                  <span className="shrink-0 text-xs font-medium text-muted-foreground">
                    {t("profile.skillsProgress", progress)}
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className="size-4 shrink-0 text-muted-foreground transition-transform group-open/item:rotate-180"
                  />
                </summary>
                <ul className="space-y-3 border-t border-border/60 px-4 py-4">
                  {group.skills.map((skill) => (
                    <li key={skill.id}>
                      <ProgressBar label={skill.title} layout="inline" value={skill.percent} />
                    </li>
                  ))}
                </ul>
              </details>
            );
          })
        ) : (
          <p className="text-sm text-muted-foreground">{t("profile.noSkillsCatalogue")}</p>
        )}
      </div>
    </section>
  );
}
